import React, { useEffect, useRef, useState } from 'react';
import Title from '../../../components/Title';
import AddButtonHolder from '../../components/Questionnaire/AddButtonHolder';
import {
  CopyWrapper,
  Description,
  HintBox,
  PageContainer,
  PreviewBox,
  Reminder,
  Section,
  SectionGroupContainer,
  StyledHeader,
  StyledTh,
} from './index.styles';
import { useAppDispatch, useAppSelector } from '../../../redux-file/hooks';
import { useGhgOrganization, useLanguage } from 'src/common/hooks';
import {
  getElecricityEmissionsData,
  getElecricityEmissionsDataEmissionShares,
  postElecricityEmissionsData,
  setSelectedDate,
} from '../../../redux-file/questionnaires/thunks';
import {
  timeConverter,
  getFirstDateOfPeriod,
} from '../../../utils/timeConverter';
import ElecricityEmissionForm from '../../components/Questionnaire/forms/elecricityEmissionForm';
import ElecricityPreview from '../../components/Questionnaire/preview/electricityPreview';
import QuestionnaireTitle from '../../components/Questionnaire/Title';
import AddProductButton from '../../../components/AddProductButton';
import QuestionnaireSectionTitle from '../../components/QuestionnaireSectionTitle';
import {
  electricity_cloud,
  heat_cloud,
  steam_cloud,
  cooling_cloud,
  ev_consumption_cloud,
  ev_distance_cloud,
} from './clouds_data';
import PageLoading from '../../../components/PageLoading';
import SomethingWentWrong from '../../../components/SomethingWentWrong';
import EvConsumptionForm from '../../components/Questionnaire/forms/ev-consumption-form';
import EvConsumptionPreview from '../../components/Questionnaire/preview/ev-consumption-preview';
import EvDistanceForm from '../../components/Questionnaire/forms/ev-distance-form';
import EvDistancePreview from '../../components/Questionnaire/preview/ev-distance-preview';
import UploadExcelButton from '../../../components/UploadExcelButton';
import { IntuitiveCalendarPeriod } from '../../components/IntuitiveCalendar';
import CopyForm from '../../components/CopyForm';
import { energy_heat_units } from '../../components/Questionnaire/forms/data';
import { Typography } from '@mui/material';
import themeColors from 'src/theme/colors';
import DataEntryCompletedCheckbox from 'src/Ghg/components/DataEntryCompletedCheckbox';
import { GhgDataEntryCompletedType } from 'src/Ghg/components/DataEntryCompletedCheckbox/interfaces';
import { withAddWrapper, withWrapper } from './Wrappers';

const units = energy_heat_units;

const WrappedElectricityEmissionForm = withWrapper(
  ElecricityEmissionForm,
  (x, _) => x
);

const electricityAttrMap = {
  electricity: 'electricity_usage',
  steam: 'steam_usage',
  heat: 'heat_usage',
  cooling: 'cooling_usage',
};
const useCombiner = <T extends { [key: string]: any[] }>(
  attr: keyof T,
  _merge: boolean = false
) => {
  return React.useCallback(
    (data: any, state: any[], merge: boolean = _merge): T => {
      if (merge) {
        return {
          ...data,
          [attr]: [...data[attr], ...state],
        };
      }
      return {
        ...data,
        [attr]: state,
      };
    },
    [attr]
  );
};

const useMergeCombiner = <T extends { [key: string]: any[] }>(
  attr: keyof T
) => {
  return useCombiner(attr, true);
};

const WrappedElectricityForm = (
  props: React.ComponentProps<typeof WrappedElectricityEmissionForm>
) => {
  const attr = electricityAttrMap[props.variant];
  const combiner = useCombiner(attr);

  return <WrappedElectricityEmissionForm {...props} combiner={combiner} />;
};

const WrappedElectricityFormAdd = withAddWrapper(WrappedElectricityForm);

const WrappedEvDistanceForm = withWrapper(
  EvDistanceForm,
  (data: any, state: any[]) => ({
    ...data,
    electric_vehicles_distancewise_usage: state,
  })
);

const WrappedEvDistanceFormAdd = withAddWrapper(WrappedEvDistanceForm);

const WrappedEvConsumptionForm = withWrapper(
  EvConsumptionForm,
  (data: any, state: any[]) => ({
    ...data,
    electric_vehicles_consumptionwise_usage: state,
  })
);

const WrappedEvConsumptionFormAdd = withAddWrapper(WrappedEvConsumptionForm);

export default function ElectricityEmission() {
  const { t, language } = useLanguage();

  const {
    selectedDate,
    electricityEmission,
    electricityEmissionError,
    electricityEmissionLoading,
    electricityEmissionEmissionShares,
  } = useAppSelector((state) => state.questionnaire);
  const organization = useGhgOrganization();
  const reportingPeriod = organization.co2.reporting_freq;

  const dispatch = useAppDispatch();
  const [active1, setActive1] = useState<undefined | number>(undefined);
  const [active2, setActive2] = useState<undefined | number>(undefined);
  const [active3, setActive3] = useState<undefined | number>(undefined);
  const [active4, setActive4] = useState<undefined | number>(undefined);
  const [active5, setActive5] = useState<undefined | number>(undefined);
  const [active6, setActive6] = useState<undefined | number>(undefined);
  const [addingEntryInSection, setAddingEntryInSection] =
    useState<GhgDataEntryCompletedType | null>(null);

  const initState = {
    emission_factor_is_known: false,
    energy_usage_data_factor_unknown: {
      spending_pln_or_kwh_per_period: '',
      unit: '',
    },
    energy_usage_data_factor_known: {
      factor: '',
      amount_per_period: '',
      unit_label: '',
    },
    certified_sources_are_utilized: false,
    certified_energy_usage_data: {
      certified_energy_type_label: '',
      certified_percentge: '100',
      emission_factor: '0',
    },
  };
  const consumptionInit = {
    emission_factor_is_known: false,
    electric_vehicle_energy_consumption_data_factor_unknown: {
      name_label: '',
      spending_pln_or_kwh_per_period: '',
      unit: '',
    },
    electric_vehicle_energy_consumption_data_factor_known: {
      emission_factor: '',
      amount: '',
      unit_label: '',
    },
    comment: '',
  };

  const distanceInit = {
    type_of_vehicle_label: '',
    comment: '',
    type_of_vehicle: '',
    unit_of_distance: '',
    distance_travelled: '',
    manual_factor: '',
  };

  const electricity_usage_ref = useRef(null);
  const steam_usage_ref = useRef(null);
  const heat_usage_ref = useRef(null);
  const cooling_usage_ref = useRef(null);
  const consumption_ref = useRef(null);
  const distance_ref = useRef(null);

  type ElectricityState = typeof initState;

  const [electricityUsageState, setElectricityUsageState] = useState<
    ElectricityState[]
  >([]);
  const [steamUsageState, setSteamUsageState] = useState<ElectricityState[]>(
    []
  );
  const [heatUsageState, setHeatUsageState] = useState<ElectricityState[]>([]);
  const [coolingUsageState, setCoolUsageState] = useState<ElectricityState[]>(
    []
  );
  const [evConspumptionState, setEvConspumptionState] = useState<
    (typeof consumptionInit)[]
  >([]);
  const [evDistanceState, setEvDistanceState] = useState<
    (typeof distanceInit)[]
  >([]);

  const combinedState = {
    electricity_usage: electricityUsageState,
    steam_usage: steamUsageState,
    heat_usage: heatUsageState,
    cooling_usage: coolingUsageState,
    electric_vehicles_consumptionwise_usage: evConspumptionState,
    electric_vehicles_distancewise_usage: evDistanceState,
  };
  type SS = typeof combinedState;

  useEffect(() => {
    setAddingEntryInSection(null);
    setElectricityUsageState(
      electricityEmission.electricity_usage.length !== 0 &&
        electricityEmissionEmissionShares.electricity_usage.length !== 0
        ? electricityEmission.electricity_usage.map((el: any, i: number) => ({
            ...el,
            emission_shared:
              electricityEmissionEmissionShares.electricity_usage[i]?.share,
          }))
        : []
    );

    setSteamUsageState(
      electricityEmission.steam_usage.length !== 0 &&
        electricityEmissionEmissionShares.steam_usage.length !== 0
        ? electricityEmission.steam_usage.map((el: any, i: number) => ({
            ...el,
            emission_shared:
              electricityEmissionEmissionShares.steam_usage[i]?.share,
          }))
        : []
    );

    setHeatUsageState(
      electricityEmission.heat_usage.length !== 0 &&
        electricityEmissionEmissionShares.heat_usage.length !== 0
        ? electricityEmission.heat_usage.map((el: any, i: number) => ({
            ...el,
            emission_shared:
              electricityEmissionEmissionShares.heat_usage[i]?.share,
          }))
        : []
    );

    setCoolUsageState(
      electricityEmission.cooling_usage.length !== 0 &&
        electricityEmissionEmissionShares.cooling_usage.length !== 0
        ? electricityEmission.cooling_usage.map((el: any, i: number) => ({
            ...el,
            emission_shared:
              electricityEmissionEmissionShares.cooling_usage[i]?.share,
          }))
        : []
    );

    setEvConspumptionState(
      electricityEmission.electric_vehicles_consumptionwise_usage.length !==
        0 &&
        electricityEmissionEmissionShares
          .electric_vehicles_consumptionwise_usage.length !== 0
        ? electricityEmission.electric_vehicles_consumptionwise_usage.map(
            (el: any, i: number) => ({
              ...el,
              emission_shared:
                electricityEmissionEmissionShares
                  .electric_vehicles_consumptionwise_usage[i]?.share,
            })
          )
        : []
    );

    setEvDistanceState(
      electricityEmission.electric_vehicles_distancewise_usage.length !== 0 &&
        electricityEmissionEmissionShares.electric_vehicles_distancewise_usage
          .length !== 0
        ? electricityEmission.electric_vehicles_distancewise_usage.map(
            (el: any, i: number) => ({
              ...el,
              emission_shared:
                electricityEmissionEmissionShares
                  .electric_vehicles_distancewise_usage[i]?.share,
            })
          )
        : []
    );
  }, [electricityEmission, selectedDate, electricityEmissionEmissionShares]);

  const refreshForm = () => {
    dispatch(
      getElecricityEmissionsData({
        orgId: organization.id,
        period: timeConverter(selectedDate, reportingPeriod),
      })
    );
    dispatch(
      getElecricityEmissionsDataEmissionShares({
        orgId: organization.id,
        period: timeConverter(selectedDate, reportingPeriod),
      })
    );
  };
  useEffect(() => {
    if (!!reportingPeriod) {
      refreshForm();
    }
  }, [selectedDate, organization.id, reportingPeriod]);

  const sendData = (data: any) => {
    return dispatch(
      postElecricityEmissionsData({
        period: timeConverter(selectedDate, reportingPeriod),
        data,
      })
    );
  };

  const handleSend = (data: any) => {
    sendData(data).then(() => refreshForm());
  };

  const combineElectricityData = useMergeCombiner<SS>('electricity_usage');
  const combineSteamData = useMergeCombiner<SS>('steam_usage');
  const combineHeatData = useMergeCombiner<SS>('heat_usage');
  const combineCoolingData = useMergeCombiner<SS>('cooling_usage');
  const combineElectricVehicleEnergyConsumptionwiseData = useMergeCombiner<SS>(
    'electric_vehicles_consumptionwise_usage'
  );
  const combineElectricVehicleEnergyDistancewiseData = useMergeCombiner<SS>(
    'electric_vehicles_distancewise_usage'
  );

  const newFormEl =
    combinedState.electricity_usage.length === 0 ||
    GhgDataEntryCompletedType.SCOPE_2_ELECTRICITY_CONSUMPTION ===
      addingEntryInSection;
  const newFormSt =
    combinedState.steam_usage.length === 0 ||
    GhgDataEntryCompletedType.SCOPE_2_STEAM === addingEntryInSection;
  const newFormHe =
    combinedState.heat_usage.length === 0 ||
    GhgDataEntryCompletedType.SCOPE_2_HEAT === addingEntryInSection;
  const newFormCo =
    combinedState.cooling_usage.length === 0 ||
    GhgDataEntryCompletedType.SCOPE_2_COOLING === addingEntryInSection;

  const newFormEVConsumption =
    combinedState.electric_vehicles_consumptionwise_usage.length === 0 ||
    GhgDataEntryCompletedType.SCOPE_2_EV_ENERGY_WISE === addingEntryInSection;

  const newFormEVDistance =
    combinedState.electric_vehicles_distancewise_usage.length === 0 ||
    GhgDataEntryCompletedType.SCOPE_2_EV_DISTANCE_WISE === addingEntryInSection;

  return (
    <PageContainer>
      <SectionGroupContainer>
        <QuestionnaireTitle info text={t('electricity-emission.title')} />

        <Description>{t('electricity-emission.description')}</Description>
        <Reminder>{t('electricity-emission.reminder1')}</Reminder>
        <Reminder>{t('electricity-emission.reminder2')}</Reminder>

        <CopyWrapper>
          <Title title={t('forms.select-period')} description={null} />
          <CopyForm
            form="ElectiricyEmissionsData"
            orgId={organization.id}
            refresh={() => refreshForm()}
          />
        </CopyWrapper>
        <IntuitiveCalendarPeriod
          reportingPeriod={reportingPeriod}
          selectedDate={getFirstDateOfPeriod(
            selectedDate ? new Date(selectedDate) : new Date(),
            reportingPeriod ?? 'M'
          )}
          setSelectedDate={(selDate: Date) =>
            dispatch(setSelectedDate(selDate))
          }
        />

        {electricityEmissionLoading && <PageLoading />}
        {electricityEmissionError && <SomethingWentWrong />}

        {!electricityEmissionLoading && !electricityEmissionError && (
          <>
            <Section>
              <div ref={electricity_usage_ref} />
              <QuestionnaireSectionTitle
                level="primary"
                text={t('electricity-emission.electricity.title')}
                boxContent={electricity_cloud}
                active={active1}
              />
              <DataEntryCompletedCheckbox
                selectedDate={selectedDate}
                type={GhgDataEntryCompletedType.SCOPE_2_ELECTRICITY_CONSUMPTION}
              />

              <PreviewBox>
                {combinedState.electricity_usage.length > 0 && (
                  <StyledHeader>
                    <StyledTh width={30}>
                      {t('electricity-emission.electricity.table.spending')}
                    </StyledTh>
                    <StyledTh width={25}>
                      {t('electricity-emission.table.cert-amount')}
                    </StyledTh>
                    <StyledTh width={20}>
                      {t('electricity-emission.table.unit')}
                    </StyledTh>
                    <StyledTh width={25}>
                      {t(
                        'direct-emission.stationary-combustion.table.emission_shared'
                      )}
                    </StyledTh>
                    <StyledTh width={0}>
                      {t('sold-products.preview.edit')}
                    </StyledTh>
                  </StyledHeader>
                )}
                <div>
                  {electricityUsageState.map((el: any, i: number) => {
                    if (active1 === i) {
                      return (
                        <WrappedElectricityForm
                          handleSend={handleSend}
                          variant="electricity"
                          combinedData={combinedState}
                          active={active1}
                          setActive={setActive1}
                          units={units}
                          id={i}
                          key={i}
                          state={electricityUsageState}
                          setState={setElectricityUsageState}
                        />
                      );
                    } else {
                      return (
                        <ElecricityPreview
                          language={language}
                          setState={setElectricityUsageState}
                          id={i}
                          state={electricityUsageState}
                          key={i}
                          setActive={setActive1}
                        />
                      );
                    }
                  })}
                </div>
                {newFormEl && (
                  <WrappedElectricityFormAdd
                    handleSend={(data: any) =>
                      handleSend(
                        combineElectricityData(
                          combinedState,
                          data.electricity_usage
                        )
                      )
                    }
                    variant="electricity"
                    active={active1}
                    setActive={setActive1}
                    units={units}
                    state={[initState]}
                  />
                )}
              </PreviewBox>
              <AddButtonHolder
                firstChild={
                  <AddProductButton
                    onClick={() => {
                      setAddingEntryInSection(
                        GhgDataEntryCompletedType.SCOPE_2_ELECTRICITY_CONSUMPTION
                      );
                      setActive1(-1);
                    }}
                  />
                }
                secondChild={
                  <UploadExcelButton
                    name={t('electricity-emission.electricity.title')}
                    slug="EnergyUsageData"
                    fullData={combinedState}
                    combineData={combineElectricityData}
                    sendData={sendData}
                    refresh={refreshForm}
                  />
                }
              />
            </Section>

            <Section>
              <div ref={heat_usage_ref} />
              <QuestionnaireSectionTitle
                level="primary"
                text={t('electricity-emission.heat.title')}
                boxContent={heat_cloud}
                active={active3}
              />
              <DataEntryCompletedCheckbox
                selectedDate={selectedDate}
                type={GhgDataEntryCompletedType.SCOPE_2_HEAT}
              />

              <PreviewBox>
                {combinedState.heat_usage.length > 0 && (
                  <StyledHeader>
                    <StyledTh width={30}>
                      {t('electricity-emission.heat.table.spending')}
                    </StyledTh>
                    <StyledTh width={25}>
                      {t('electricity-emission.table.cert-amount')}
                    </StyledTh>
                    <StyledTh width={20}>
                      {t('electricity-emission.table.unit')}
                    </StyledTh>
                    <StyledTh width={25}>
                      {t(
                        'direct-emission.stationary-combustion.table.emission_shared'
                      )}
                    </StyledTh>
                    <StyledTh width={0}>
                      {t('sold-products.preview.edit')}
                    </StyledTh>
                  </StyledHeader>
                )}
                <div>
                  {heatUsageState.map((el: any, i: number) => {
                    if (active3 === i) {
                      return (
                        <WrappedElectricityEmissionForm
                          handleSend={handleSend}
                          variant="heat"
                          combinedData={combinedState}
                          active={active3}
                          setActive={setActive3}
                          units={units}
                          id={i}
                          key={i}
                          state={heatUsageState}
                          setState={setHeatUsageState}
                        />
                      );
                    } else {
                      return (
                        <ElecricityPreview
                          language={language}
                          setState={setHeatUsageState}
                          id={i}
                          state={heatUsageState}
                          key={i}
                          setActive={setActive3}
                        />
                      );
                    }
                  })}
                  {newFormHe && (
                    <WrappedElectricityFormAdd
                      handleSend={(data: any) =>
                        handleSend(
                          combineHeatData(combinedState, data.heat_usage)
                        )
                      }
                      variant="heat"
                      active={active3}
                      setActive={setActive3}
                      units={units}
                      state={[initState]}
                    />
                  )}
                </div>
              </PreviewBox>
              <AddButtonHolder
                firstChild={
                  <AddProductButton
                    onClick={() => {
                      setAddingEntryInSection(
                        GhgDataEntryCompletedType.SCOPE_2_HEAT
                      );
                      setActive3(-1);
                    }}
                  />
                }
                secondChild={
                  <UploadExcelButton
                    name={t('electricity-emission.heat.title')}
                    slug="EnergyUsageData"
                    fullData={combinedState}
                    combineData={combineHeatData}
                    sendData={sendData}
                    refresh={refreshForm}
                  />
                }
              />
            </Section>

            <Section>
              <div ref={cooling_usage_ref} />
              <QuestionnaireSectionTitle
                level="primary"
                text={t('electricity-emission.cooling.title')}
                boxContent={cooling_cloud}
                active={active4}
              />
              <DataEntryCompletedCheckbox
                selectedDate={selectedDate}
                type={GhgDataEntryCompletedType.SCOPE_2_COOLING}
              />

              <PreviewBox>
                {combinedState.cooling_usage.length > 0 && (
                  <StyledHeader>
                    <StyledTh width={30}>
                      {t('electricity-emission.cooling.table.spending')}
                    </StyledTh>
                    <StyledTh width={25}>
                      {t('electricity-emission.table.cert-amount')}
                    </StyledTh>
                    <StyledTh width={20}>
                      {t('electricity-emission.table.unit')}
                    </StyledTh>
                    <StyledTh width={25}>
                      {t(
                        'direct-emission.stationary-combustion.table.emission_shared'
                      )}
                    </StyledTh>

                    <StyledTh width={0}>
                      {t('sold-products.preview.edit')}
                    </StyledTh>
                  </StyledHeader>
                )}
                <div>
                  {coolingUsageState.map((el: any, i: number) => {
                    if (active4 === i) {
                      return (
                        <WrappedElectricityEmissionForm
                          handleSend={handleSend}
                          variant="cooling"
                          combinedData={combinedState}
                          active={active4}
                          setActive={setActive4}
                          units={units}
                          id={i}
                          key={i}
                          state={coolingUsageState}
                          setState={setCoolUsageState}
                        />
                      );
                    } else {
                      return (
                        <ElecricityPreview
                          language={language}
                          setState={setCoolUsageState}
                          id={i}
                          state={coolingUsageState}
                          key={i}
                          setActive={setActive4}
                        />
                      );
                    }
                  })}
                </div>
                {newFormCo && (
                  <WrappedElectricityFormAdd
                    handleSend={(data: any) =>
                      handleSend(
                        combineCoolingData(combinedState, data.cooling_usage)
                      )
                    }
                    variant="cooling"
                    active={active4}
                    setActive={setActive4}
                    units={units}
                    state={[initState]}
                  />
                )}
              </PreviewBox>
              <AddButtonHolder
                firstChild={
                  <AddProductButton
                    onClick={() => {
                      setAddingEntryInSection(
                        GhgDataEntryCompletedType.SCOPE_2_COOLING
                      );
                      setActive4(-1);
                    }}
                  />
                }
                secondChild={
                  <UploadExcelButton
                    name={t('electricity-emission.cooling.title')}
                    slug="EnergyUsageData"
                    fullData={combinedState}
                    combineData={combineCoolingData}
                    sendData={sendData}
                    refresh={refreshForm}
                  />
                }
              />
            </Section>
            <Section>
              <QuestionnaireSectionTitle
                level="primary"
                text={t('electricity-emission.vehicles.title')}
                active={false}
              />
              <Section>
                <HintBox>
                  <span>{t('hint')}</span>
                  <p
                    dangerouslySetInnerHTML={{
                      __html: t('direct-emission.mobile-combustion.hint.line1'),
                    }}
                  />
                  <div>
                    <p
                      dangerouslySetInnerHTML={{
                        __html: t(
                          'direct-emission.mobile-combustion.hint.line2'
                        ),
                      }}
                    />
                    <p
                      dangerouslySetInnerHTML={{
                        __html: t(
                          'direct-emission.mobile-combustion.hint.line3'
                        ),
                      }}
                    />
                  </div>
                </HintBox>
              </Section>
              <Section>
                <div ref={consumption_ref} />
                <QuestionnaireSectionTitle
                  level="secondary"
                  text={t('electricity-emission.vehicles.consumption.title')}
                  boxContent={ev_consumption_cloud}
                  active={active5}
                />
                <DataEntryCompletedCheckbox
                  selectedDate={selectedDate}
                  type={GhgDataEntryCompletedType.SCOPE_2_EV_ENERGY_WISE}
                />

                <PreviewBox>
                  {combinedState.electric_vehicles_consumptionwise_usage
                    .length > 0 && (
                    <StyledHeader>
                      <StyledTh width={25}>
                        {t(
                          'electricity-emission.vehicles.consumption.table.name'
                        )}
                      </StyledTh>
                      <StyledTh width={15}>
                        {t(
                          'electricity-emission.vehicles.consumption.table.amount'
                        )}
                      </StyledTh>
                      <StyledTh width={20}>
                        {t('electricity-emission.table.cert-amount')}
                      </StyledTh>
                      <StyledTh width={10}>
                        {t(
                          'electricity-emission.vehicles.consumption.table.unit'
                        )}
                      </StyledTh>
                      <StyledTh width={20}>
                        {t(
                          'direct-emission.stationary-combustion.table.emission_shared'
                        )}
                      </StyledTh>
                      <StyledTh width={0}>
                        {t('sold-products.preview.edit')}
                      </StyledTh>
                    </StyledHeader>
                  )}
                  <div>
                    {evConspumptionState.map((el: any, i: number) => {
                      if (active5 === i) {
                        return (
                          <WrappedEvConsumptionForm
                            handleSend={handleSend}
                            combinedData={combinedState}
                            active={active5}
                            setActive={setActive5}
                            id={i}
                            key={i}
                            state={evConspumptionState}
                            setState={setEvConspumptionState}
                          />
                        );
                      } else {
                        return (
                          <EvConsumptionPreview
                            language={language}
                            setState={setEvConspumptionState}
                            id={i}
                            state={evConspumptionState}
                            key={i}
                            setActive={setActive5}
                          />
                        );
                      }
                    })}
                  </div>
                  {newFormEVConsumption && (
                    <WrappedEvConsumptionFormAdd
                      handleSend={(data) =>
                        handleSend(
                          combineElectricVehicleEnergyConsumptionwiseData(
                            combinedState,
                            data.electric_vehicles_consumptionwise_usage
                          )
                        )
                      }
                      active={active5}
                      setActive={setActive5}
                      state={[consumptionInit]}
                    />
                  )}
                </PreviewBox>
                <AddButtonHolder
                  firstChild={
                    <AddProductButton
                      onClick={() => {
                        setAddingEntryInSection(
                          GhgDataEntryCompletedType.SCOPE_2_EV_ENERGY_WISE
                        );
                        setActive5(-1);
                      }}
                    />
                  }
                  secondChild={
                    <UploadExcelButton
                      name={t('electricity-emission.vehicles.title')}
                      slug="ElectricVehicleEnergyConsumptionwiseData"
                      fullData={combinedState}
                      combineData={
                        combineElectricVehicleEnergyConsumptionwiseData
                      }
                      sendData={sendData}
                      refresh={refreshForm}
                    />
                  }
                />
              </Section>

              <Section>
                <div ref={distance_ref} />
                <QuestionnaireSectionTitle
                  level="secondary"
                  text={t('electricity-emission.vehicles.distance.title')}
                  boxContent={ev_distance_cloud}
                  active={active6}
                />
                <DataEntryCompletedCheckbox
                  selectedDate={selectedDate}
                  type={GhgDataEntryCompletedType.SCOPE_2_EV_DISTANCE_WISE}
                />

                <PreviewBox>
                  {combinedState.electric_vehicles_distancewise_usage.length >
                    0 && (
                    <StyledHeader>
                      <StyledTh width={25}>
                        {t(
                          'electricity-emission.vehicles.distance.type_of_vehicle_label'
                        )}
                      </StyledTh>
                      <StyledTh width={20}>
                        {t(
                          'electricity-emission.vehicles.distance.table.distance_travelled'
                        )}
                      </StyledTh>
                      <StyledTh width={15}>
                        {t(
                          'electricity-emission.vehicles.distance.table.unit_of_distance'
                        )}
                      </StyledTh>
                      <StyledTh width={25}>
                        {t(
                          'electricity-emission.vehicles.distance.type_of_vehicle'
                        )}
                      </StyledTh>
                      <StyledTh width={15}>
                        {t(
                          'direct-emission.stationary-combustion.table.emission_shared'
                        )}
                      </StyledTh>

                      <StyledTh width={0}>
                        {t('sold-products.preview.edit')}
                      </StyledTh>
                    </StyledHeader>
                  )}
                  <div>
                    {evDistanceState.map((el: any, i: number) => {
                      if (active6 === i) {
                        return (
                          <WrappedEvDistanceForm
                            handleSend={handleSend}
                            combinedData={combinedState}
                            active={active6}
                            setActive={setActive6}
                            id={i}
                            key={i}
                            state={evDistanceState}
                            setState={setEvDistanceState}
                          />
                        );
                      } else {
                        return (
                          <EvDistancePreview
                            language={language}
                            setState={setEvDistanceState}
                            id={i}
                            state={evDistanceState}
                            key={i}
                            setActive={setActive6}
                          />
                        );
                      }
                    })}
                  </div>
                  {newFormEVDistance && (
                    <WrappedEvDistanceFormAdd
                      handleSend={(data) =>
                        handleSend(
                          combineElectricVehicleEnergyDistancewiseData(
                            combinedState,
                            data.electric_vehicles_distancewise_usage
                          )
                        )
                      }
                      active={active6}
                      setActive={setActive6}
                      state={[distanceInit]}
                    />
                  )}
                </PreviewBox>
                <AddButtonHolder
                  firstChild={
                    <AddProductButton
                      onClick={() => {
                        setAddingEntryInSection(
                          GhgDataEntryCompletedType.SCOPE_2_EV_DISTANCE_WISE
                        );
                        setActive6(-1);
                      }}
                    />
                  }
                  secondChild={
                    <UploadExcelButton
                      name={t('electricity-emission.vehicles.distance.title')}
                      slug="ElectricVehicleEnergyDistancewiseData"
                      fullData={combinedState}
                      combineData={combineElectricVehicleEnergyDistancewiseData}
                      sendData={sendData}
                      refresh={refreshForm}
                    />
                  }
                />
              </Section>

              <Section>
                <div ref={steam_usage_ref} />
                <QuestionnaireSectionTitle
                  level="primary"
                  text={t('electricity-emission.steam.title')}
                  boxContent={steam_cloud}
                  active={active2}
                />

                <DataEntryCompletedCheckbox
                  selectedDate={selectedDate}
                  type={GhgDataEntryCompletedType.SCOPE_2_STEAM}
                />

                <PreviewBox>
                  {combinedState.steam_usage.length > 0 && (
                    <StyledHeader>
                      <StyledTh width={30}>
                        {t('electricity-emission.steam.table.spending')}
                      </StyledTh>
                      <StyledTh width={25}>
                        {t('electricity-emission.table.cert-amount')}
                      </StyledTh>
                      <StyledTh width={20}>
                        {t('electricity-emission.table.unit')}
                      </StyledTh>
                      <StyledTh width={25}>
                        {t(
                          'direct-emission.stationary-combustion.table.emission_shared'
                        )}
                      </StyledTh>
                      <StyledTh width={0}>
                        {t('sold-products.preview.edit')}
                      </StyledTh>
                    </StyledHeader>
                  )}
                  <div>
                    {steamUsageState.map((el: any, i: number) => {
                      if (active2 === i) {
                        return (
                          <WrappedElectricityEmissionForm
                            handleSend={handleSend}
                            variant="steam"
                            combinedData={combinedState}
                            active={active2}
                            setActive={setActive2}
                            units={units}
                            id={i}
                            key={i}
                            state={steamUsageState}
                            setState={setSteamUsageState}
                          />
                        );
                      } else {
                        return (
                          <ElecricityPreview
                            language={language}
                            setState={setSteamUsageState}
                            id={i}
                            state={steamUsageState}
                            key={i}
                            setActive={setActive2}
                          />
                        );
                      }
                    })}
                  </div>
                  {newFormSt && (
                    <WrappedElectricityFormAdd
                      handleSend={(data: any) =>
                        handleSend(
                          combineSteamData(combinedState, data.steam_usage)
                        )
                      }
                      variant="steam"
                      active={active2}
                      setActive={setActive2}
                      units={units}
                      state={[initState]}
                    />
                  )}
                </PreviewBox>
                <AddButtonHolder
                  firstChild={
                    <AddProductButton
                      onClick={() => {
                        setAddingEntryInSection(
                          GhgDataEntryCompletedType.SCOPE_2_STEAM
                        );
                        setActive2(-1);
                      }}
                    />
                  }
                  secondChild={
                    <UploadExcelButton
                      name={t('electricity-emission.steam.title')}
                      slug="EnergyUsageData"
                      fullData={combinedState}
                      combineData={combineSteamData}
                      sendData={sendData}
                      refresh={refreshForm}
                    />
                  }
                />
              </Section>
            </Section>
          </>
        )}
      </SectionGroupContainer>
    </PageContainer>
  );
}
